import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  ShoppingCart, Search, Plus, Minus, Trash2, CreditCard, Banknote, 
  Smartphone, Pause, Play, Receipt as ReceiptIcon, X, Check, Calculator, User, UserPlus, Phone, Printer, Monitor,
  Percent, RotateCcw, ArrowLeftRight, FileSearch, Tag, FileDown, List
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/AuthContext';
import { useStoreTerminal } from '@/components/store/TerminalSelector';
import { useNavigate } from 'react-router-dom';
import { posAPI, customerAPI, storeSyncAPI, businessSettingsAPI, storesAPI, offersAPI } from '@/services/api';
import { Switch } from '@/components/ui/switch';
import Receipt from '@/components/Receipt';
import A4GSTInvoice from '@/components/A4GSTInvoice';
import ReturnRefundDialog from '@/components/store/ReturnRefundDialog';
import ExchangeDialog from '@/components/store/ExchangeDialog';
import html2pdf from 'html2pdf.js';
import HighlightText from '@/components/HighlightText';
import { todayIST } from '@/utils/dateFormat';

const POSBillingPage = () => {
  // Cart state
  const [cartItems, setCartItems] = useState([]);
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerGstin, setCustomerGstin] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerState, setCustomerState] = useState('');
  const [isGstBill, setIsGstBill] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null); // null = Guest Sales
  
  // Customer search/add
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [searchingCustomer, setSearchingCustomer] = useState(false);
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerMobile, setNewCustomerMobile] = useState('');
  const [addingCustomer, setAddingCustomer] = useState(false);

  // Pre-payment customer prompt (shown on Pay click in Guest mode)
  const [prePayOpen, setPrePayOpen] = useState(false);
  const [prePayMobile, setPrePayMobile] = useState('');
  const [prePayName, setPrePayName] = useState('');
  const [prePayResults, setPrePayResults] = useState([]);
  const [prePaySearching, setPrePaySearching] = useState(false);
  const [prePaySavingNew, setPrePaySavingNew] = useState(false);
  const prePayMobileRef = useRef(null);
  const prePayNameRef = useRef(null);
  // Flag to prevent onOpenChange from re-triggering payment after an explicit action.
  const prePayDispatchedRef = useRef(false);
  
  // Register state
  const [register, setRegister] = useState(null);
  const [registerLoading, setRegisterLoading] = useState(true);
  
  // Product search
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [highlightedSearchIndex, setHighlightedSearchIndex] = useState(0);
  const [searching, setSearching] = useState(false);
  const searchReqRef = useRef(0);
  
  // Barcode input
  const [barcodeInput, setBarcodeInput] = useState('');
  const barcodeRef = useRef(null);
  const lastEnterPressRef = useRef(0); // Track last Enter press time for double-Enter feature
  
  // Payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [payments, setPayments] = useState([]);
  const [cashReceived, setCashReceived] = useState('');
  const [cardAmount, setCardAmount] = useState('');
  const [cardReference, setCardReference] = useState('');
  const [upiAmount, setUpiAmount] = useState('');
  const [upiReference, setUpiReference] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentTxnId, setPaymentTxnId] = useState(null);
  const submittingRef = useRef(false);
  
  // Hold bill dialog
  const [holdDialogOpen, setHoldDialogOpen] = useState(false);
  const [holdNote, setHoldNote] = useState('');
  
  // Held bills dialog
  const [heldBillsDialogOpen, setHeldBillsDialogOpen] = useState(false);
  const [heldBills, setHeldBills] = useState([]);
  
  // Success/Receipt dialog
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [lastSaleFull, setLastSaleFull] = useState(null); // Full sale data for receipt
  const [storeInfo, setStoreInfo] = useState(null);
  const receiptRef = useRef(null);
  const a4InvoiceRef = useRef(null);
  const [gstInvoiceSettings, setGstInvoiceSettings] = useState(null);
  
  // Payment dialog refs for keyboard navigation
  const productSearchRef = useRef(null);
  const cashInputRef = useRef(null);
  const cardAmountRef = useRef(null);
  const cardRefRef = useRef(null);
  const upiAmountRef = useRef(null);
  const upiRefRef = useRef(null);
  const completeSaleButtonRef = useRef(null);
  
  // Bill Discount state
  const [billDiscount, setBillDiscount] = useState({ type: 'percent', value: 0 });
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountType, setDiscountType] = useState('percent'); // 'percent' or 'amount'
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [discountSettings, setDiscountSettings] = useState({ allow_bill_discount: false, max_discount_percent: 0 });

  // Offer picker state (shown when a line has >1 eligible offers)
  const [offerPickerOpen, setOfferPickerOpen] = useState(false);
  const [offerPickerIndex, setOfferPickerIndex] = useState(null);
  
  // Re-print / Return / Exchange state
  const [billLookupDialogOpen, setBillLookupDialogOpen] = useState(false);
  const [billLookupQuery, setBillLookupQuery] = useState('');
  const [billsList, setBillsList] = useState([]);
  const [loadingBills, setLoadingBills] = useState(false);
  const [selectedBillForAction, setSelectedBillForAction] = useState(null);
  const [billAction, setBillAction] = useState('reprint'); // 'reprint', 'return', 'exchange'
  const [billDateFrom, setBillDateFrom] = useState(() => todayIST());
  const [billDateTo, setBillDateTo] = useState(() => todayIST());
  
  // Return/Exchange Dialog state
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [exchangeDialogOpen, setExchangeDialogOpen] = useState(false);
  const [selectedBillForReturn, setSelectedBillForReturn] = useState(null);
  const [selectedBillForExchange, setSelectedBillForExchange] = useState(null);
  
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const terminalContext = useStoreTerminal();
  
  // Check if user is manager (not cashier)
  const isManager = user?.permissions?.length > 2 || 
    user?.permissions?.some(p => ['sales', 'dashboard', '*'].includes(typeof p === 'string' ? p : p?.module));
  
  const storeId = user?.entity_id;
  
  // Get selected terminal
  const selectedTerminalId = terminalContext?.selectedTerminal;
  const currentTerminal = terminalContext?.currentTerminal;

  // Fetch store info for receipt
  const fetchStoreInfo = useCallback(async () => {
    if (!storeId) return;
    try {
      const [storeRes, brandingRes, discountRes, gstSettingsRes] = await Promise.all([
        storeSyncAPI.getStoreInfo(storeId),
        businessSettingsAPI.getPublicBranding(),
        storesAPI.getDiscountSettings(storeId),
        storesAPI.getGstInvoiceSettings(storeId).catch(() => ({ data: null }))
      ]);
      setStoreInfo({
        ...storeRes.data?.store,
        ...brandingRes.data
      });
      // Set discount settings
      setDiscountSettings(discountRes.data || { allow_bill_discount: false, max_discount_percent: 0 });
      // Set GST invoice settings
      setGstInvoiceSettings(gstSettingsRes.data || null);
    } catch (error) {
      console.error('Failed to fetch store info:', error);
    }
  }, [storeId]);

  useEffect(() => {
    fetchStoreInfo();
  }, [fetchStoreInfo]);

  // Download A4 GST Invoice as PDF
  const downloadA4GSTInvoice = useCallback(() => {
    if (!a4InvoiceRef.current || !lastSaleFull) return;
    
    const element = a4InvoiceRef.current;
    const billNum = lastSaleFull.bill_number || 'BILL';
    const billDate = lastSaleFull.bill_datetime ? new Date(lastSaleFull.bill_datetime).toLocaleDateString('en-GB').replace(/\//g, '-') : new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
    const filename = `${billNum}-${billDate}.pdf`;
    
    const opt = {
      margin: 0,
      filename: filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };
    
    html2pdf().set(opt).from(element).save();
  }, [lastSaleFull]);

  // Reset POS state after a bill is finalized so the next bill starts fresh.
  // Clears selected customer (back to Guest), bill discount, GST flag, and
  // the receipt preview; focuses the barcode scanner for the next scan.
  const resetAfterBill = useCallback(() => {
    setSuccessDialogOpen(false);
    setLastSaleFull(null);
    setLastSale(null);
    setSelectedCustomer(null);
    setCustomerPhone('');
    setCustomerName('');
    setCustomerGstin('');
    setCustomerAddress('');
    setCustomerState('');
    setIsGstBill(false);
    setBillDiscount({ type: 'percent', value: 0 });
    setDiscountValue('');
    setDiscountReason('');
    setDiscountType('percent');
    setTimeout(() => barcodeRef.current?.focus(), 60);
  }, []);

  // Handle Enter key for printing
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (successDialogOpen && e.key === 'Enter') {
        e.preventDefault();
        handlePrintReceipt();
        setTimeout(() => resetAfterBill(), 500);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [successDialogOpen, resetAfterBill]);

  // Print receipt
  const handlePrintReceipt = () => {
    if (!receiptRef.current) return;
    const printContent = receiptRef.current.innerHTML;
    const printWindow = window.open('', '_blank', 'width=300,height=600');
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head>
          <title>Receipt</title>
          <style>
            body { 
              font-family: monospace; 
              font-size: 10px; 
              margin: 0; 
              padding: 5px;
              width: 72mm;
            }
            .print-hide { display: none !important; }
            @page { size: 72mm auto; margin: 0; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();

    // Wait for ALL images (logo + QR) to finish loading before printing.
    // Without this, on a 2nd/3rd print the fixed timeout fires before the
    // 3rd-party QR image has downloaded, and the QR prints blank.
    let printed = false;
    const doPrint = () => {
      if (printed) return;
      printed = true;
      try {
        printWindow.print();
      } finally {
        setTimeout(() => printWindow.close(), 300);
      }
    };
    const images = Array.from(printWindow.document.images || []);
    if (images.length === 0) {
      setTimeout(doPrint, 150);
      return;
    }
    let remaining = images.length;
    const settle = () => { if (--remaining <= 0) doPrint(); };
    images.forEach(img => {
      if (img.complete && img.naturalWidth > 0) {
        settle();
      } else {
        img.addEventListener('load', settle, { once: true });
        img.addEventListener('error', settle, { once: true });
      }
    });
    // Safety net — never let the popup hang longer than 4s
    setTimeout(doPrint, 4000);
  };

  // Check if register is open
  const checkRegister = useCallback(async () => {
    if (!storeId) return;
    
    setRegisterLoading(true);
    try {
      // Use terminal ID as counter if available
      const counterId = selectedTerminalId || 'C01';
      const res = await posAPI.getCurrentRegister(storeId, counterId);
      setRegister(res.data?.register);
      if (!res.data?.is_open) {
        toast({ 
          title: 'Register Closed', 
          description: 'Please open the cash register first', 
          variant: 'destructive' 
        });
      }
    } catch (error) {
      console.error('Failed to check register:', error);
    } finally {
      setRegisterLoading(false);
    }
  }, [storeId, selectedTerminalId, toast]);

  useEffect(() => {
    checkRegister();
    // Focus barcode input on mount
    barcodeRef.current?.focus();
  }, [checkRegister]);

  // Global keyboard shortcuts for keyboard-first POS flow
  // F2 → open product name search · F3 → focus barcode scan
  useEffect(() => {
    const onKey = (e) => {
      // Ignore if a dialog (payment/discount/held/etc.) is open to avoid stealing focus
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      if (e.key === 'F2') {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => productSearchRef.current?.focus(), 60);
      } else if (e.key === 'F3') {
        e.preventDefault();
        barcodeRef.current?.focus();
        barcodeRef.current?.select?.();
      } else if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false);
        setTimeout(() => barcodeRef.current?.focus(), 50);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searchOpen]);

  // Search products
  const searchProducts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    const reqId = ++searchReqRef.current;
    try {
      const res = await posAPI.searchProducts(query.trim(), storeId);
      if (reqId !== searchReqRef.current) return;
      setSearchResults(res.data || []);
      setHighlightedSearchIndex(0);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  }, [storeId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchProducts(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchProducts]);

  // Search customers by mobile or name
  const searchCustomers = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setCustomerSearchResults([]);
      return;
    }
    
    setSearchingCustomer(true);
    try {
      // Check if query is a number (mobile) or text (name)
      const isNumeric = /^\d+$/.test(query);
      const searchParams = isNumeric 
        ? { mobile: query, limit: 10 }
        : { name: query, limit: 10 };
      
      const res = await customerAPI.search(searchParams);
      setCustomerSearchResults(res.data || []);
    } catch (error) {
      console.error('Customer search failed:', error);
    } finally {
      setSearchingCustomer(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchCustomers(customerSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearchQuery, searchCustomers]);

  // Select customer
  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerPhone(customer.mobile);
    setCustomerName(customer.name);
    setCustomerGstin(customer.gstin || '');
    setCustomerAddress(customer.address || '');
    setCustomerState(customer.state || '');
    setCustomerSearchOpen(false);
    setCustomerSearchQuery('');
    toast({ title: 'Customer Selected', description: `${customer.name} (${customer.mobile})` });
  };

  // Clear customer (back to Guest)
  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerPhone('');
    setCustomerName('');
    setCustomerGstin('');
    setCustomerAddress('');
    setCustomerState('');
    setIsGstBill(false);
  };

  // Add new customer
  const addNewCustomer = async () => {
    if (!newCustomerMobile || newCustomerMobile.length < 10) {
      toast({ title: 'Error', description: 'Please enter a valid mobile number', variant: 'destructive' });
      return;
    }
    if (!newCustomerName.trim()) {
      toast({ title: 'Error', description: 'Please enter customer name', variant: 'destructive' });
      return;
    }

    setAddingCustomer(true);
    try {
      const res = await customerAPI.create({
        name: newCustomerName.trim(),
        mobile: newCustomerMobile,
        store_id: storeId
      });
      const newCust = res.data.customer;
      selectCustomer(newCust);
      setAddCustomerOpen(false);
      setNewCustomerName('');
      setNewCustomerMobile('');
      toast({ title: 'Success', description: 'Customer added successfully' });
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to add customer', 
        variant: 'destructive' 
      });
    } finally {
      setAddingCustomer(false);
    }
  };

  // Handle barcode scan/entry with double-Enter support
  const handleBarcodeSubmit = async (e) => {
    e.preventDefault();
    
    // Check for double-Enter on empty input to open payment dialog
    const now = Date.now();
    if (!barcodeInput.trim()) {
      if (now - lastEnterPressRef.current < 500 && cartItems.length > 0) {
        // Double-Enter detected with items in cart - open payment dialog
        setPaymentDialogOpen(true);
        lastEnterPressRef.current = 0; // Reset
        return;
      }
      lastEnterPressRef.current = now;
      return;
    }
    
    // Reset double-enter tracking when there's actual input
    lastEnterPressRef.current = 0;
    
    try {
      const res = await posAPI.lookupProduct(barcodeInput.trim(), storeId);
      if (res.data.found) {
        addToCart(res.data.product);
      } else if (res.data.multiple) {
        // Multiple matches - open the search popover with the same payload the search
        // box uses (correct price/stock). Focus must move INTO the popover, otherwise
        // re-focusing the barcode input closes it instantly.
        const scanned = barcodeInput.trim();
        setBarcodeInput('');
        setSearchQuery(scanned);
        try {
          const searchRes = await posAPI.searchProducts(scanned, storeId);
          setSearchResults(searchRes.data || []);
        } catch (err) {
          setSearchResults([]);
        }
        setSearchOpen(true);
        setTimeout(() => productSearchRef.current?.focus(), 120);
        return;
      } else {
        toast({ title: 'Not Found', description: 'Product not found', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to lookup product', variant: 'destructive' });
    }
    
    setBarcodeInput('');
    barcodeRef.current?.focus();
  };

  // Add product to cart
  const addToCart = async (product) => {
    // STRICT VALIDATION: Check if selling price exceeds MRP
    const sellingPrice = product.selling_price || 0;
    const mrp = product.mrp || sellingPrice;
    
    if (sellingPrice > mrp && mrp > 0) {
      toast({
        title: '⚠️ Price Error',
        description: `Cannot add "${product.name}" - Selling price (₹${sellingPrice.toFixed(2)}) exceeds MRP (₹${mrp.toFixed(2)}). Please contact HQ to fix pricing.`,
        variant: 'destructive',
        duration: 8000
      });
      return;
    }
    
    // STOCK VALIDATION: Check available stock
    const availableStock = product.stock_qty || 0;
    const existingIndex = cartItems.findIndex(item => item.product_id === product.id);
    const currentQtyInCart = existingIndex >= 0 ? cartItems[existingIndex].quantity : 0;
    const requestedQty = currentQtyInCart + 1;
    
    // Check if product is out of stock
    if (availableStock <= 0) {
      toast({
        title: '🚫 Out of Stock',
        description: `"${product.name}" is out of stock. Cannot add to bill.`,
        variant: 'destructive',
        duration: 5000
      });
      return;
    }
    
    // Check if requested quantity exceeds available stock
    if (requestedQty > availableStock) {
      toast({
        title: '⚠️ Insufficient Stock',
        description: `"${product.name}" - Only ${availableStock} units available. You already have ${currentQtyInCart} in cart.`,
        variant: 'destructive',
        duration: 5000
      });
      return;
    }
    
    if (existingIndex >= 0) {
      // Increase quantity
      const updated = [...cartItems];
      updated[existingIndex] = reconsiderOffers(existingIndex, syncOfferDiscount({ ...updated[existingIndex], quantity: (Number(updated[existingIndex].quantity) || 0) + 1 }));
      updated[existingIndex] = calculateItemTotals(updated[existingIndex]);
      // Update the available stock in the item for future checks
      updated[existingIndex].available_stock = availableStock;
      setCartItems(updated);
    } else {
      // Add new item
      const newItem = {
        product_id: product.id,
        sku: product.sku,
        barcode: product.barcode,
        product_name: product.name,
        hsn_code: product.hsn_code || '',
        uom: product.uom,
        quantity: 1,
        unit_price: product.selling_price,
        mrp: product.mrp || product.selling_price,
        discount_amount: 0,
        tax_rate: product.tax_rate || 0,
        cgst_rate: product.cgst_rate || 0,
        sgst_rate: product.sgst_rate || 0,
        tax_group_name: product.tax_group_name || '',
        // UOM conversion info for decimal qty
        base_uom: product.base_uom || 'PCS',
        purchase_uom: product.purchase_uom || null,
        uom_conversion_factor: product.uom_conversion_factor || 1,
        allow_decimal_qty: (product.uom_conversion_factor || 1) > 1,
        // Store available stock for quantity validation
        available_stock: availableStock,
        // Offer slots (filled by applyAutoOffer)
        available_offers: [],
        applied_offer: null,
      };
      const withOffer = await applyAutoOffer(newItem);
      setCartItems([...cartItems, calculateItemTotals(withOffer)]);
      if ((withOffer.available_offers || []).length > 1 && !withOffer.applied_offer_id) {
        // Several offers: ask right away in a modal so the cashier never misses one
        setOfferPickerIndex(cartItems.length);
        setOfferPickerOpen(true);
      } else if (withOffer.applied_offer_id) {
        const isSlab = withOffer.applied_offer?.offer_type === 'qty_slab';
        toast({
          title: isSlab ? `Bulk offer: ${withOffer.applied_offer_name}` : `Offer applied: ${withOffer.applied_offer_name}`,
          description: isSlab
            ? `${slabLabel(withOffer.applied_offer, 1, null)} — increase qty to unlock`
            : `Line discount ₹${(withOffer.applied_offer?.line_discount || 0).toFixed(2)}`,
        });
      }
    }
    
    setSearchOpen(false);
    setSearchQuery('');
    barcodeRef.current?.focus();
  };

  // ---- OFFER INTEGRATION ----
  // Payment mode driving qty-slab offer prices (cash vs card/upi). Follows the largest payment
  // once payments are added; before that the cashier can pick it in the payment dialog.
  const [offerPayMode, setOfferPayMode] = useState('cash');

  const slabTier = (offer, qty) => {
    const tiers = (offer.tiers || []).filter(t => Number(t.min_qty) <= qty);
    return tiers.length ? tiers.reduce((a, b) => (Number(a.min_qty) >= Number(b.min_qty) ? a : b)) : null;
  };

  const slabRate = (tier, mrp, payMode) => {
    const ov = tier.payment_modes || {};
    const value = Number(payMode && ov[payMode] != null ? ov[payMode] : tier.value) || 0;
    const minQty = Number(tier.min_qty) || 1;
    switch (tier.value_type || 'total_price') {
      case 'total_price': return value / minQty;
      case 'unit_price': return value;
      case 'flat_discount': return Math.max(0, mrp - value);
      case 'percent': return Math.max(0, mrp * (1 - value / 100));
      default: return mrp;
    }
  };

  // Human-readable slab summary for the given payment mode, e.g. "24 pcs → ₹1400 (cash)".
  const slabLabel = (offer, qty, payMode) => {
    const tiers = [...(offer.tiers || [])].sort((a, b) => Number(a.min_qty) - Number(b.min_qty));
    const tier = slabTier(offer, qty) || tiers[0];
    if (!tier) return '';
    const fmt = (v) => tier.value_type === 'percent' ? `${v}%` : `₹${Number(v).toLocaleString()}`;
    const hasOverride = payMode && tier.payment_modes?.[payMode] != null;
    const base = Number(hasOverride ? tier.payment_modes[payMode] : tier.value);
    const unit = tier.value_type === 'unit_price' ? '/pc' : tier.value_type === 'flat_discount' ? ' off/pc' : tier.value_type === 'percent' ? ' off' : '';
    return `${Number(tier.min_qty)} pcs → ${fmt(base)}${unit}${hasOverride ? ` (${payMode})` : ''}`;
  };

  // Compute per-line discount from a saved offer (mirrors backend _compute_line_discount).
  // Used on quantity changes so we don't need to hit the API every ± tap.
  const computeOfferDiscount = (offer, unitPrice, qty, mrp, payMode = offerPayMode) => {
    if (!offer) return 0;
    const sp = Math.max(0, Number(unitPrice) || 0);
    const m = Math.max(sp, Number(mrp) || sp);
    const q = Number(qty) || 0;
    let d = 0;
    if (offer.offer_type === 'flat_discount') {
      // Flat ₹/unit off MRP; effective rate clamped at SP (ceiling).
      const rateFromMrp = Math.max(0, m - (Number(offer.discount_value) || 0));
      const effRate = Math.min(sp, rateFromMrp);
      d = Math.max(0, sp - effRate) * q;
    } else if (offer.offer_type === 'percentage_discount') {
      // % off MRP; effective rate clamped at SP (ceiling).
      const pct = Number(offer.discount_value) || 0;
      const rateFromMrp = Math.max(0, m * (1 - pct / 100));
      const effRate = Math.min(sp, rateFromMrp);
      const raw = Math.max(0, sp - effRate) * q;
      d = offer.max_discount ? Math.min(raw, Number(offer.max_discount)) : raw;
    } else if (offer.offer_type === 'buy_x_get_y') {
      const buy = Number(offer.buy_qty) || 0;
      const get = Number(offer.get_qty) || 0;
      if (buy > 0 && q >= buy && (buy + get) > 0) {
        d = Math.floor(q / (buy + get)) * get * sp;
      }
    } else if (offer.offer_type === 'qty_slab') {
      const tier = slabTier(offer, q);
      if (tier) d = Math.max(0, sp - Math.min(sp, slabRate(tier, m, payMode))) * q;
    }
    return Math.round(Math.max(d, 0) * 100) / 100;
  };

  // Re-derive the line's offer discount from its CURRENT quantity (single source of truth
  // for every qty-change path: scan, +/-, typed qty, offer pick, payment mode change).
  const syncOfferDiscount = (item, payMode = offerPayMode) => {
    if (!item.applied_offer) return item;
    const prev = item.applied_offer.line_discount || 0;
    const next = computeOfferDiscount(item.applied_offer, item.unit_price, item.quantity, item.mrp, payMode);
    return {
      ...item,
      discount_amount: Math.max((item.discount_amount || 0) - prev + next, 0),
      applied_offer: { ...item.applied_offer, line_discount: next },
    };
  };

  // True only when an unlocked slab tier actually prices differently by payment mode.
  const hasPayModeOffer = cartItems.some(i => {
    if (i.applied_offer?.offer_type !== 'qty_slab') return false;
    const tier = slabTier(i.applied_offer, Number(i.quantity) || 0);
    return !!tier && Object.keys(tier.payment_modes || {}).length > 0;
  });

  // Re-price every slab line for a payment mode (called from the Pay-by selector / payments list).
  const changeOfferPayMode = (mode) => {
    if (mode === offerPayMode) return;
    setOfferPayMode(mode);
    setCartItems(prev => prev.map(i => i.applied_offer?.offer_type === 'qty_slab'
      ? calculateItemTotals(syncOfferDiscount(i, mode)) : i));
  };

  // Grand total the bill would come to under a given payment mode (for the Pay-by selector).
  const grandTotalForMode = (mode) => {
    const items = cartItems.map(i => i.applied_offer?.offer_type === 'qty_slab'
      ? calculateItemTotals(syncOfferDiscount(i, mode)) : i);
    const total = items.reduce((s, i) => s + (i.total_amount || 0), 0);
    return Math.round(total - billDiscountAmount);
  };

  // Fetch applicable offers for a cart line and auto-apply when there's exactly one.
  // Mutates & returns the item.
  const applyAutoOffer = async (item) => {
    try {
      const { data } = await offersAPI.getForProduct(
        item.product_id, storeId, item.quantity, item.unit_price, item.mrp
      );
      const offers = data?.offers || [];
      item.available_offers = offers;
      if (offers.length === 1) {
        const o = offers[0];
        item.applied_offer_id = o.id;
        item.applied_offer_code = o.code;
        item.applied_offer_name = o.name;
        item.applied_offer = o;
        item.discount_amount = (item.discount_amount || 0) + (o.line_discount || 0);
      } else {
        item.applied_offer_id = null;
      }
    } catch (e) {
      item.available_offers = [];
    }
    return item;
  };

  // Best offer for a line at its CURRENT qty (null when nothing gives a discount).
  const bestOfferFor = (line, payMode = offerPayMode) => {
    let best = null;
    for (const o of line.available_offers || []) {
      const d = computeOfferDiscount(o, line.unit_price, line.quantity, line.mrp, payMode);
      if (d > 0 && (!best || d > best.d)) best = { id: o.id, d };
    }
    return best;
  };

  // After a qty edit: if a DIFFERENT offer now beats the applied one, re-open the picker so the
  // cashier re-applies consciously (prompted once per best-offer change, not on every ± tap).
  const reconsiderOffers = (index, line) => {
    if ((line.available_offers || []).length < 2) return line;
    const best = bestOfferFor(line);
    const appliedD = line.applied_offer ? (line.applied_offer.line_discount || 0) : 0;
    if (best && best.id !== line.applied_offer_id && best.d > appliedD + 0.009 && line.last_prompted_best !== best.id) {
      setTimeout(() => { setOfferPickerIndex(index); setOfferPickerOpen(true); }, 0);
      return { ...line, last_prompted_best: best.id };
    }
    return line;
  };

  // User picks one offer from the available_offers list for a given line.
  const applyChosenOffer = (lineIndex, offerId) => {
    const updated = [...cartItems];
    const line = { ...updated[lineIndex] };
    // Remove previous offer discount
    if (line.applied_offer) {
      line.discount_amount = Math.max((line.discount_amount || 0) - (line.applied_offer.line_discount || 0), 0);
    }
    const picked = (line.available_offers || []).find(o => o.id === offerId);
    if (picked) {
      line.applied_offer = picked;
      line.applied_offer_id = picked.id;
      line.applied_offer_code = picked.code;
      line.applied_offer_name = picked.name;
      // Recompute from current qty so it's accurate even after +/- changes
      const d = computeOfferDiscount(picked, line.unit_price, line.quantity, line.mrp);
      line.discount_amount = (line.discount_amount || 0) + d;
      line.applied_offer.line_discount = d;
    } else {
      line.applied_offer = null;
      line.applied_offer_id = null;
      line.applied_offer_code = null;
      line.applied_offer_name = null;
    }
    updated[lineIndex] = calculateItemTotals(line);
    setCartItems(updated);
    setOfferPickerOpen(false);
    setOfferPickerIndex(null);
  };

  // Calculate item totals - GST INCLUSIVE (GST is included in selling price)
  const calculateItemTotals = (item) => {
    const lineTotal = item.quantity * item.unit_price;  // This is the final price (GST included)
    const discountedTotal = lineTotal - item.discount_amount;  // After line discount
    
    // GST Inclusive calculation: Extract tax from the total
    const taxRate = item.tax_rate || 0;
    let taxableAmount, cgstAmount, sgstAmount, taxAmount;
    
    if (taxRate > 0) {
      // Reverse calculate: Base = Total / (1 + rate/100)
      taxableAmount = Math.round(discountedTotal / (1 + taxRate / 100) * 100) / 100;
      taxAmount = Math.round((discountedTotal - taxableAmount) * 100) / 100;
      cgstAmount = Math.round(taxAmount / 2 * 100) / 100;
      sgstAmount = taxAmount - cgstAmount;  // Ensures exact split
    } else {
      taxableAmount = discountedTotal;
      taxAmount = 0;
      cgstAmount = 0;
      sgstAmount = 0;
    }
    
    // Total amount is the discounted total (GST already included)
    const totalAmount = discountedTotal;
    
    // Calculate discount from MRP
    const mrpTotal = item.quantity * (item.mrp || item.unit_price);
    const mrpDiscount = mrpTotal - lineTotal;
    const discountPercent = item.mrp > 0 ? ((item.mrp - item.unit_price) / item.mrp * 100) : 0;
    
    return {
      ...item,
      taxable_amount: taxableAmount,
      cgst_amount: cgstAmount,
      sgst_amount: sgstAmount,
      tax_amount: taxAmount,
      total_amount: totalAmount,
      mrp_discount: mrpDiscount,
      discount_percent: discountPercent
    };
  };

  // Update item quantity (for +/- buttons)
  const updateQuantity = (index, delta) => {
    const updated = [...cartItems];
    const item = updated[index];
    
    // For products with UOM conversion, use smaller increments
    const increment = item.allow_decimal_qty ? 0.1 : 1;
    const actualDelta = delta * (item.allow_decimal_qty ? increment : 1);
    
    let newQty = (Number(item.quantity) || 0) + actualDelta;
    // Minimum qty: 0.1 for decimal, 1 for regular
    const minQty = item.allow_decimal_qty ? 0.1 : 1;
    newQty = Math.max(minQty, Math.round(newQty * 100) / 100);
    
    // STOCK VALIDATION: Check if increasing quantity exceeds available stock
    const availableStock = item.available_stock || 0;
    if (delta > 0 && availableStock > 0 && newQty > availableStock) {
      toast({
        title: '⚠️ Insufficient Stock',
        description: `"${item.product_name}" - Only ${availableStock} units available in stock.`,
        variant: 'destructive',
        duration: 4000
      });
      return;
    }
    
    updated[index] = reconsiderOffers(index, syncOfferDiscount({ ...item, quantity: newQty }));
    updated[index] = calculateItemTotals(updated[index]);
    setCartItems(updated);
  };

  // Direct qty edit (works for all products, supports decimals)
  const updateQuantityDirect = (index, newQty) => {
    const updated = [...cartItems];
    const item = updated[index];
    
    // Allow empty or partial decimal input while typing
    if (newQty === '' || newQty === '.') {
      updated[index].quantity = newQty;
      setCartItems(updated);
      return;
    }
    
    // Parse the value
    let qty = parseFloat(newQty);
    
    // If not a valid number, keep the raw string (for typing)
    if (isNaN(qty)) {
      updated[index].quantity = newQty;
      setCartItems(updated);
      return;
    }
    
    // Valid number - update with calculations
    if (qty <= 0) qty = 0.1;
    
    // Round to 2 decimal places for display
    qty = Math.round(qty * 100) / 100;
    
    // STOCK VALIDATION: Check if entered quantity exceeds available stock
    const availableStock = item.available_stock || 0;
    if (availableStock > 0 && qty > availableStock) {
      toast({
        title: '⚠️ Insufficient Stock',
        description: `"${item.product_name}" - Only ${availableStock} units available. Setting to max available.`,
        variant: 'destructive',
        duration: 4000
      });
      qty = availableStock;
    }
    
    updated[index] = reconsiderOffers(index, syncOfferDiscount({ ...item, quantity: qty }));
    updated[index] = calculateItemTotals(updated[index]);
    setCartItems(updated);
  };

  // Remove item from cart
  const removeItem = (index) => {
    setCartItems(cartItems.filter((_, i) => i !== index));
  };

  // Calculate cart totals - GST Inclusive
  const cartTotals = cartItems.reduce((totals, item) => ({
    subtotal: totals.subtotal + item.total_amount,  // Total including GST
    discount: totals.discount + item.discount_amount,
    taxable: totals.taxable + item.taxable_amount,  // Base amount (excluding GST)
    cgst: totals.cgst + item.cgst_amount,
    sgst: totals.sgst + item.sgst_amount,
    tax: totals.tax + item.tax_amount,
    total: totals.total + item.total_amount,
    mrpTotal: totals.mrpTotal + (item.quantity * (item.mrp || item.unit_price)),
    savings: totals.savings + (item.mrp_discount || 0)
  }), { subtotal: 0, discount: 0, taxable: 0, cgst: 0, sgst: 0, tax: 0, total: 0, mrpTotal: 0, savings: 0 });

  // Calculate bill-level discount
  const calculateBillDiscount = () => {
    if (!billDiscount.value || billDiscount.value <= 0) return 0;
    if (billDiscount.type === 'percent') {
      return Math.round(cartTotals.total * billDiscount.value / 100 * 100) / 100;
    }
    return Math.min(billDiscount.value, cartTotals.total);
  };
  
  const billDiscountAmount = calculateBillDiscount();

  // Calculate tax-group-wise summary
  const taxGroupSummary = cartItems.reduce((summary, item) => {
    const rate = item.tax_rate || 0;
    const groupName = item.tax_group_name || (rate > 0 ? `GST ${rate}%` : 'Exempt');
    if (!summary[groupName]) {
      summary[groupName] = { rate, taxable: 0, cgst: 0, sgst: 0, total: 0 };
    }
    summary[groupName].taxable += item.taxable_amount;
    summary[groupName].cgst += item.cgst_amount;
    summary[groupName].sgst += item.sgst_amount;
    summary[groupName].total += item.tax_amount;
    return summary;
  }, {});

  const grandTotal = Math.round(cartTotals.total - billDiscountAmount);
  const roundOff = grandTotal - cartTotals.total;

  // Open payment dialog
  const openPaymentDialog = () => {
    if (cartItems.length === 0) {
      toast({ title: 'Error', description: 'Add items to cart first', variant: 'destructive' });
      return;
    }
    setPayments([]);
    setPaymentTxnId((typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
    setCashReceived('');
    setCardAmount('');
    setCardReference('');
    setUpiAmount('');
    setUpiReference('');
    setPaymentDialogOpen(true);
  };

  // Pay button click handler.
  // - If a customer is already selected -> go straight to payment dialog.
  // - Otherwise, prompt cashier to search/add customer (or skip with Esc).
  const handlePayClick = () => {
    if (cartItems.length === 0) {
      toast({ title: 'Error', description: 'Add items to cart first', variant: 'destructive' });
      return;
    }
    if (selectedCustomer) {
      openPaymentDialog();
      return;
    }
    setPrePayMobile('');
    setPrePayName('');
    setPrePayResults([]);
    setPrePaySearching(false);
    prePayDispatchedRef.current = false;
    setPrePayOpen(true);
  };

  // Debounced auto-search by mobile inside the pre-payment prompt.
  useEffect(() => {
    if (!prePayOpen) return;
    const q = (prePayMobile || '').trim();
    if (q.length < 3) {
      setPrePayResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setPrePaySearching(true);
        const res = await customerAPI.search({ mobile: q, limit: 8 });
        setPrePayResults(res.data || []);
      } catch (err) {
        console.error('Pre-pay customer search failed:', err);
      } finally {
        setPrePaySearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [prePayMobile, prePayOpen]);

  // Pick an existing customer from the pre-pay search results and continue to payment.
  const prePaySelectAndPay = (customer) => {
    selectCustomer(customer);
    prePayDispatchedRef.current = true;
    setPrePayOpen(false);
    setTimeout(() => openPaymentDialog(), 50);
  };

  // Create a brand-new customer from the pre-pay prompt and continue to payment.
  const prePayCreateAndPay = async () => {
    const mobile = (prePayMobile || '').trim();
    const name = (prePayName || '').trim();
    if (mobile.length < 10) {
      toast({ title: 'Error', description: 'Enter a valid 10-digit mobile number', variant: 'destructive' });
      prePayMobileRef.current?.focus();
      return;
    }
    if (!name) {
      toast({ title: 'Error', description: 'Enter customer name', variant: 'destructive' });
      prePayNameRef.current?.focus();
      return;
    }
    setPrePaySavingNew(true);
    try {
      const res = await customerAPI.create({ name, mobile, store_id: storeId });
      const newCust = res.data.customer;
      selectCustomer(newCust);
      prePayDispatchedRef.current = true;
      setPrePayOpen(false);
      toast({ title: 'Customer Added', description: `${newCust.name} (${newCust.mobile})` });
      setTimeout(() => openPaymentDialog(), 50);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to add customer',
        variant: 'destructive'
      });
    } finally {
      setPrePaySavingNew(false);
    }
  };

  // Skip the customer prompt and bill as Guest (Esc key or Skip button).
  const prePaySkipAsGuest = () => {
    prePayDispatchedRef.current = true;
    setPrePayOpen(false);
    setTimeout(() => openPaymentDialog(), 50);
  };

  // Add payment
  const addPayment = (mode, amount, reference = '') => {
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) return false;
    
    setPayments(prev => [...prev, { mode, amount: Math.round(amt * 100) / 100, reference }]);
    if (mode === 'cash') setCashReceived('');
    if (mode === 'card') { setCardAmount(''); setCardReference(''); }
    if (mode === 'upi') { setUpiAmount(''); setUpiReference(''); }
    return true;
  };

  // Add a payment line and jump to the next sensible field.
  // Empty amount + Enter → do NOT auto-fill balance; just move cursor to next field.
  // This lets cashiers split payments freely without accidental full-balance rows.
  const addAndAdvance = (mode, amount, reference, nextRef) => {
    const amt = parseFloat(amount);
    if (!amount || isNaN(amt) || amt <= 0) {
      // Skip: move focus onwards, don't add a payment
      setTimeout(() => nextRef?.current?.focus(), 60);
      return;
    }
    // Slab offers priced by payment mode: work out which mode will dominate once this payment
    // lands, switch the cart to it FIRST, and cap against that mode's total — otherwise typing the
    // full card/UPI total while the cart still shows the cheaper cash price gets capped ₹50 short.
    let due = balanceDue;
    if (hasPayModeOffer) {
      const totals = [...payments, { mode, amount: amt }].reduce((m, p) => ({ ...m, [p.mode]: (m[p.mode] || 0) + p.amount }), {});
      const projected = Object.keys(totals).reduce((a, b) => (totals[a] >= totals[b] ? a : b));
      if (projected !== offerPayMode) {
        changeOfferPayMode(projected);
        due = grandTotalForMode(projected) - totalPaid;
      }
    }
    const capped = Math.min(amt, mode === 'cash' ? amt : due);
    const added = addPayment(mode, capped, reference || '');
    if (!added) return;
    const remaining = Math.round((due - Math.min(amt, due)) * 100) / 100;
    setTimeout(() => {
      if (remaining <= 0) completeSaleButtonRef.current?.focus();
      else nextRef?.current?.focus();
    }, 60);
  };

  // Remove payment
  const removePayment = (index) => {
    setPayments(payments.filter((_, i) => i !== index));
  };

  // Calculate total paid and balance
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const balanceDue = grandTotal - totalPaid;

  // Slab offers priced by payment mode follow the mode carrying the largest payment
  // (same rule as the backend `dominant_payment_mode`).
  useEffect(() => {
    if (!payments.length || !hasPayModeOffer) return;
    const totals = payments.reduce((m, p) => ({ ...m, [p.mode]: (m[p.mode] || 0) + p.amount }), {});
    const dominant = Object.keys(totals).reduce((a, b) => (totals[a] >= totals[b] ? a : b));
    if (dominant && dominant !== offerPayMode) changeOfferPayMode(dominant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments]);

  // When payment dialog is open and balance drops to 0 after an entry, auto-focus
  // the Complete Sale button so pressing Enter finalizes & prints the receipt.
  useEffect(() => {
    if (paymentDialogOpen && balanceDue <= 0 && grandTotal > 0 && payments.length > 0) {
      setTimeout(() => completeSaleButtonRef.current?.focus(), 60);
    }
  }, [balanceDue, paymentDialogOpen, grandTotal, payments.length]);

  // Complete sale
  const completeSale = async () => {
    // Hard re-entry guard: Enter key auto-repeat / click+keydown used to fire this twice,
    // and the 2nd call went out with an EMPTY cart but the old payments → ₹0 ghost bills
    if (submittingRef.current || processingPayment) return;
    if (cartItems.length === 0) {
      toast({ title: 'Error', description: 'Cart is empty', variant: 'destructive' });
      setPaymentDialogOpen(false);
      return;
    }
    if (balanceDue > 0) {
      toast({ title: 'Error', description: 'Payment incomplete', variant: 'destructive' });
      return;
    }
    
    // Validate GST bill requirements
    if (isGstBill && (!customerGstin || !customerState)) {
      toast({ title: 'Error', description: 'GST Bill requires customer GSTIN and State', variant: 'destructive' });
      return;
    }
    
    submittingRef.current = true;
    setProcessingPayment(true);
    try {
      const saleData = {
        store_id: storeId,
        counter_id: selectedTerminalId || register?.counter_id || 'C01',
        client_txn_id: paymentTxnId,
        items: cartItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          discount_amount: item.discount_amount,
          applied_offer_id: item.applied_offer_id || null,
          applied_offer_code: item.applied_offer_code || null,
          applied_offer_name: item.applied_offer_name || null,
          is_return: item.is_return || false,
          is_exchange: item.is_exchange || false,
          original_bill: item.original_bill || null
        })),
        payments: payments.map(p => ({
          mode: p.mode,
          amount: p.amount,
          reference: p.reference || null
        })),
        customer_id: selectedCustomer?.id || null,
        customer_phone: customerPhone || null,
        customer_name: customerName || null,
        customer_gstin: customerGstin || null,
        customer_address: customerAddress || null,
        customer_state: customerState || null,
        is_gst_bill: isGstBill,
        bill_discount: billDiscountAmount > 0 ? {
          type: billDiscount.type,
          value: billDiscount.value,
          amount: billDiscountAmount,
          reason: billDiscount.reason || null
        } : null
      };
      
      const res = await posAPI.createSale(saleData);
      
      // Record purchase for customer (if selected)
      if (selectedCustomer?.id && res.data.grand_total) {
        try {
          await customerAPI.update(selectedCustomer.id, {});
        } catch (err) {
          console.error('Failed to record customer purchase:', err);
        }
      }
      
      // Store full sale data for receipt
      setLastSaleFull(res.data);
      setLastSale({
        bill_number: res.data.bill_number,
        grand_total: res.data.grand_total,
        change_amount: res.data.change_amount
      });
      
      // Clear cart + payment session so nothing can be re-submitted
      setCartItems([]);
      setPayments([]);
      setOfferPayMode('cash');
      setPaymentTxnId(null);
      setPaymentDialogOpen(false);
      setSuccessDialogOpen(true);
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to complete sale', 
        variant: 'destructive' 
      });
    } finally {
      submittingRef.current = false;
      setProcessingPayment(false);
    }
  };

  // Hold bill (allowed for both named customers and guest sales)
  const holdBill = async () => {
    if (cartItems.length === 0) return;

    try {
      await posAPI.holdBill({
        store_id: storeId,
        counter_id: selectedTerminalId || register?.counter_id || 'C01',
        items: cartItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          mrp: item.mrp,
          discount_amount: item.discount_amount || 0,
          tax_rate: item.tax_rate || 0,
          cgst_rate: item.cgst_rate || 0,
          sgst_rate: item.sgst_rate || 0
        })),
        customer_phone: customerPhone || null,
        customer_name: customerName || null,
        hold_note: holdNote || null
      });
      
      toast({ title: 'Success', description: 'Bill held successfully' });
      setCartItems([]);
      setCustomerPhone('');
      setCustomerName('');
      setHoldDialogOpen(false);
      setHoldNote('');
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to hold bill', variant: 'destructive' });
    }
  };

  // Fetch held bills
  const fetchHeldBills = async () => {
    try {
      const res = await posAPI.getHeldBills(storeId);
      setHeldBills(res.data || []);
      setHeldBillsDialogOpen(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch held bills', variant: 'destructive' });
    }
  };

  // Recall held bill
  const recallHeldBill = async (bill) => {
    // Load items from held bill
    const items = bill.items.map(item => calculateItemTotals({
      product_id: item.product_id,
      sku: item.sku,
      barcode: item.barcode,
      product_name: item.product_name,
      hsn_code: item.hsn_code || '',
      uom: item.uom,
      quantity: item.quantity,
      unit_price: item.unit_price,
      mrp: item.mrp,
      discount_amount: item.discount_amount || 0,
      tax_rate: item.tax_rate || 0,
      cgst_rate: item.cgst_rate ?? ((item.tax_rate || 0) / 2),
      sgst_rate: item.sgst_rate ?? ((item.tax_rate || 0) / 2)
    }));
    
    setCartItems(items);
    setCustomerPhone(bill.customer_phone || '');
    setCustomerName(bill.customer_name || '');
    
    // Delete held bill
    try {
      await posAPI.deleteHeldBill(bill.id);
    } catch (error) {
      console.error('Failed to delete held bill:', error);
    }
    
    setHeldBills(prev => prev.filter(b => b.id !== bill.id));
    setHeldBillsDialogOpen(false);
    toast({ title: 'Success', description: 'Bill recalled' });
  };

  // Apply bill discount
  const applyDiscount = () => {
    // Check if discount is allowed for this store
    if (!discountSettings.allow_bill_discount) {
      toast({ title: 'Error', description: 'Bill discount is not allowed for this store', variant: 'destructive' });
      return;
    }
    
    const value = parseFloat(discountValue) || 0;
    if (value <= 0) {
      toast({ title: 'Error', description: 'Enter a valid discount value', variant: 'destructive' });
      return;
    }
    
    // Validate against max discount percentage
    let effectivePercent = value;
    if (discountType === 'amount') {
      // Calculate the percentage for amount discount
      effectivePercent = (value / cartTotals.total) * 100;
    }
    
    const maxPercent = discountSettings.max_discount_percent || 0;
    if (effectivePercent > maxPercent) {
      toast({ 
        title: 'Error', 
        description: `Discount cannot exceed ${maxPercent}% (Maximum allowed)`, 
        variant: 'destructive' 
      });
      return;
    }
    
    if (discountType === 'percent' && value > 100) {
      toast({ title: 'Error', description: 'Discount cannot exceed 100%', variant: 'destructive' });
      return;
    }
    if (discountType === 'amount' && value > cartTotals.total) {
      toast({ title: 'Error', description: 'Discount cannot exceed bill total', variant: 'destructive' });
      return;
    }
    setBillDiscount({ type: discountType, value, reason: discountReason });
    setDiscountDialogOpen(false);
    setDiscountValue('');
    setDiscountReason('');
    toast({ title: 'Success', description: `Discount of ${discountType === 'percent' ? value + '%' : '₹' + value} applied` });
  };

  // Clear bill discount
  const clearDiscount = () => {
    setBillDiscount({ type: 'percent', value: 0 });
    toast({ title: 'Discount removed' });
  };

  // Fetch bills list for re-print/return/exchange
  const fetchBillsList = async () => {
    setLoadingBills(true);
    try {
      const res = await posAPI.listSales({
        store_id: storeId,
        from_date: billDateFrom,
        to_date: billDateTo,
        search: billLookupQuery || undefined,
        limit: 50
      });
      setBillsList(res.data?.items || res.data || []);
    } catch (error) {
      console.error('Failed to fetch bills:', error);
      toast({ title: 'Error', description: 'Failed to load bills', variant: 'destructive' });
    } finally {
      setLoadingBills(false);
    }
  };

  // Open bill lookup dialog
  const openBillLookup = (action) => {
    setBillAction(action);
    setBillLookupQuery('');
    setSelectedBillForAction(null);
    setBillsList([]);
    // Set today's date
    const today = todayIST();
    setBillDateFrom(today);
    setBillDateTo(today);
    setBillLookupDialogOpen(true);
    // Fetch today's bills
    setTimeout(() => fetchBillsList(), 100);
  };

  // Handle re-print
  const handleReprint = (bill) => {
    if (bill) {
      setLastSaleFull(bill);
      setSuccessDialogOpen(true);
      setBillLookupDialogOpen(false);
    }
  };

  // Handle return - open Return dialog
  const handleReturn = (bill) => {
    if (!bill) return;
    setSelectedBillForReturn(bill);
    setBillLookupDialogOpen(false);
    setReturnDialogOpen(true);
  };

  // Handle exchange - open Exchange dialog
  const handleExchange = (bill) => {
    if (!bill) return;
    setSelectedBillForExchange(bill);
    setBillLookupDialogOpen(false);
    setExchangeDialogOpen(true);
  };

  // Callback when return is successful
  const handleReturnSuccess = (result) => {
    toast({ 
      title: 'Return Processed', 
      description: `Return ${result.return_number} completed. Refund: ₹${result.total_refund?.toFixed(2)}` 
    });
    // Refresh register data to update cash totals
    checkRegister();
  };

  // Callback when exchange is successful
  const handleExchangeSuccess = (result) => {
    toast({ 
      title: 'Exchange Processed', 
      description: `Exchange ${result.exchange_number} completed.` 
    });
    // Refresh register data to update cash totals
    checkRegister();
  };

  // If register is not open, show message
  if (!registerLoading && !register) {
    return (
      <div className="p-6 lg:p-8 bg-gray-50 min-h-screen flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Cash Register Closed</h2>
            <p className="text-gray-500 mb-4">Please open the cash register to start billing</p>
            <Button onClick={() => navigate('/store/register')} className="bg-emerald-500 hover:bg-emerald-600">
              Open Register
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-100 flex flex-col" data-testid="pos-billing-page">
      <Toaster />
      
      {/* Header */}
      <div className="bg-emerald-700 text-white p-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShoppingCart className="w-6 h-6" />
          <div>
            <h1 className="font-bold">POS Billing</h1>
            <p className="text-xs text-emerald-200">{user?.entity_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {currentTerminal ? (
            <Badge className="bg-emerald-600 flex items-center gap-1">
              <Monitor className="w-3 h-3" />
              {currentTerminal.terminal_id}
            </Badge>
          ) : (
            <Badge className="bg-emerald-600">{register?.counter_id}</Badge>
          )}
          <Badge className="bg-emerald-600">{register?.shift_id}</Badge>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={fetchHeldBills}
            className="text-white hover:bg-emerald-600"
            data-testid="held-bills-btn"
          >
            <List className="w-4 h-4 mr-1" /> Held Bills
            {heldBills.length > 0 && (
              <span className="ml-1 px-1.5 rounded-full bg-white text-emerald-700 text-xs font-semibold">
                {heldBills.length}
              </span>
            )}
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              if (cartItems.length === 0) {
                toast({ title: 'Cart is empty', description: 'Add items before holding a bill' });
                return;
              }
              setHoldDialogOpen(true);
            }}
            className="text-white hover:bg-emerald-600"
            data-testid="hold-bill-top-btn"
          >
            <Pause className="w-4 h-4 mr-1" /> Hold
          </Button>
          {/* Reprint is available to cashiers and managers alike -- per
              user policy any register-holder can reprint any bill from
              that register. Each reprint is still logged server-side. */}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => openBillLookup('reprint')}
            className="text-white hover:bg-emerald-600"
            data-testid="reprint-btn"
          >
            <Printer className="w-4 h-4 mr-1" /> Re-print
          </Button>
          {isManager && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => openBillLookup('return')}
              className="text-white hover:bg-emerald-600"
              data-testid="return-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1" /> Return
            </Button>
          )}
          {isManager && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => openBillLookup('exchange')}
              className="text-white hover:bg-emerald-600"
              data-testid="exchange-btn"
            >
              <ArrowLeftRight className="w-4 h-4 mr-1" /> Exchange
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Cart */}
        <div className="flex-1 flex flex-col bg-white border-r border-gray-200">
          {/* Barcode Input */}
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <form onSubmit={handleBarcodeSubmit} className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  ref={barcodeRef}
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  placeholder="Scan barcode or enter SKU...  (F3)"
                  className="pl-10 pr-16 bg-white border-gray-300"
                  autoFocus
                  data-testid="barcode-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 font-mono bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200 pointer-events-none">
                  F2 search
                </span>
              </div>
              <Popover open={searchOpen} onOpenChange={setSearchOpen}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" className="border-gray-300" data-testid="product-search-trigger">
                    <Search className="w-4 h-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-96 p-0 bg-white border-gray-200" align="end">
                  <div className="p-3 border-b border-gray-200">
                    <Input 
                      ref={productSearchRef}
                      placeholder="Search products by name, SKU or barcode... (↑↓ to navigate, Enter to select)" 
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          setHighlightedSearchIndex(i => Math.min(i + 1, Math.max(searchResults.length - 1, 0)));
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          setHighlightedSearchIndex(i => Math.max(i - 1, 0));
                        } else if (e.key === 'Enter') {
                          e.preventDefault();
                          const pick = searchResults[highlightedSearchIndex];
                          if (pick && (pick.stock_qty || 0) > 0) {
                            addToCart(pick);
                          }
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setSearchOpen(false);
                          setTimeout(() => barcodeRef.current?.focus(), 50);
                        }
                      }}
                      className="bg-gray-50 border-gray-300"
                      autoFocus
                      data-testid="product-search-input"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {searching ? (
                      <div className="p-4 text-center text-gray-500">Searching...</div>
                    ) : searchResults.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        {searchQuery.length < 2 ? 'Type at least 2 characters to search' : `No product matches "${searchQuery}"`}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {searchResults.map((product, idx) => {
                          const isOutOfStock = (product.stock_qty || 0) <= 0;
                          const isActive = idx === highlightedSearchIndex;
                          return (
                            <div
                              key={product.id}
                              data-testid={`pos-product-option-${product.id}`}
                              onClick={() => !isOutOfStock && addToCart(product)}
                              onMouseEnter={() => setHighlightedSearchIndex(idx)}
                              ref={isActive ? (el) => el?.scrollIntoView({ block: 'nearest' }) : undefined}
                              className={`p-3 flex items-center justify-between ${
                                isOutOfStock 
                                  ? 'bg-red-50 cursor-not-allowed opacity-70' 
                                  : isActive
                                    ? 'bg-emerald-100 cursor-pointer ring-1 ring-emerald-400'
                                    : 'hover:bg-emerald-50 cursor-pointer'
                              }`}
                            >
                              <div className="flex-1">
                                <p className={`font-medium ${isOutOfStock ? 'text-gray-500' : 'text-gray-800'}`}>
                                  <HighlightText text={product.name} query={searchQuery} />
                                </p>
                                <p className="text-xs text-gray-500">
                                  <HighlightText text={product.sku} query={searchQuery} />
                                  {product.barcode && <> | <HighlightText text={product.barcode} query={searchQuery} /></>}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className={`font-bold ${isOutOfStock ? 'text-gray-400' : 'text-emerald-600'}`}>
                                  ₹{product.selling_price}
                                </p>
                                <p className={`text-xs ${
                                  isOutOfStock 
                                    ? 'text-red-500 font-medium' 
                                    : product.stock_qty < 5 
                                      ? 'text-amber-500' 
                                      : 'text-gray-400'
                                }`}>
                                  {isOutOfStock ? '🚫 Out of Stock' : `Stock: ${product.stock_qty}`}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </form>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-3">
            {cartItems.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <ShoppingCart className="w-16 h-16 mx-auto mb-2" />
                  <p>Scan or search to add items</p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {cartItems.map((item, index) => (
                  <div 
                    key={index} 
                    className="flex flex-col p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 truncate">{item.product_name}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                          <span className="text-xs text-gray-500">SKU: {item.sku}</span>
                          {item.hsn_code && <span className="text-xs text-gray-500">HSN: {item.hsn_code}</span>}
                          <span className="text-xs text-blue-600 font-medium" data-testid={`cart-line-gst-${index}`}>GST: {item.tax_rate || 0}%</span>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                          <span className="text-xs text-gray-500">MRP: ₹{(item.mrp || item.unit_price).toFixed(2)}</span>
                          <span className="text-xs text-emerald-600">Price: ₹{item.unit_price.toFixed(2)}</span>
                          {item.discount_percent > 0 && (
                            <span className="text-xs text-orange-600 font-medium">-{item.discount_percent.toFixed(0)}% off</span>
                          )}
                        </div>
                        {/* Offer badge / picker trigger */}
                        {(item.available_offers && item.available_offers.length > 0) && (
                          <div className="mt-1.5">
                            {item.applied_offer_id ? (
                              <button
                                type="button"
                                onClick={() => { setOfferPickerIndex(index); setOfferPickerOpen(true); }}
                                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-200"
                                data-testid={`offer-applied-${index}`}
                              >
                                🎁 {item.applied_offer_name} · -₹{(item.applied_offer?.line_discount || 0).toFixed(2)}
                                {item.applied_offer?.offer_type === 'qty_slab' && (
                                  <span className="opacity-80" data-testid={`offer-slab-hint-${index}`}>
                                    · {slabLabel(item.applied_offer, Number(item.quantity) || 0, hasPayModeOffer ? offerPayMode : null)}
                                    {!slabTier(item.applied_offer, Number(item.quantity) || 0) && ' · add qty to unlock'}
                                  </span>
                                )}
                                {item.available_offers.length > 1 && <span className="opacity-75">· change</span>}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => { setOfferPickerIndex(index); setOfferPickerOpen(true); }}
                                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 hover:bg-amber-200 animate-pulse"
                                data-testid={`offer-choose-${index}`}
                              >
                                🎁 {item.available_offers.length} offer{item.available_offers.length > 1 ? 's' : ''} available — tap to apply
                              </button>
                            )}
                          </div>
                        )}
                        {/* UOM Conversion Info */}
                        {item.allow_decimal_qty && (
                          <div className="mt-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded inline-block">
                            1 {item.purchase_uom} = {item.uom_conversion_factor} {item.base_uom}
                            <span className="ml-2 text-gray-500">
                              (Qty {item.quantity} = {(item.quantity * item.uom_conversion_factor).toFixed(1)} {item.base_uom})
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateQuantity(index, -1)}
                          data-testid={`cart-qty-minus-${index}`}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <input
                          type="text"
                          inputMode="decimal"
                          pattern="[0-9]*\.?[0-9]*"
                          value={item.quantity}
                          onChange={(e) => {
                            // Allow typing decimal values like .1, 0.1, 2.5
                            const value = e.target.value;
                            if (value === '' || value === '.' || /^\d*\.?\d*$/.test(value)) {
                              updateQuantityDirect(index, value);
                            }
                          }}
                          onBlur={(e) => {
                            // On blur, ensure valid number
                            const qty = parseFloat(e.target.value);
                            if (isNaN(qty) || qty <= 0) {
                              updateQuantityDirect(index, item.allow_decimal_qty ? '0.1' : '1');
                            }
                          }}
                          className="w-20 text-center font-medium border rounded px-2 py-1 text-sm"
                          data-testid={`cart-qty-input-${index}`}
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateQuantity(index, 1)}
                          data-testid={`cart-qty-plus-${index}`}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="w-24 text-right font-bold text-gray-800">
                        ₹{item.total_amount.toFixed(2)}
                      </p>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-600"
                        onClick={() => removeItem(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Customer Info */}
          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Customer</span>
            </div>
            {selectedCustomer ? (
              <div className="flex items-center justify-between p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-emerald-800">{selectedCustomer.name}</p>
                    <p className="text-xs text-emerald-600">{selectedCustomer.mobile}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearCustomer}
                  className="h-7 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Popover open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="flex-1 justify-start text-gray-500 border-gray-300"
                      data-testid="customer-search-btn"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Guest Sales
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0 bg-white border-gray-200" align="start">
                    <div className="p-3 border-b border-gray-200">
                      <Input 
                        placeholder="Search by mobile or name..." 
                        value={customerSearchQuery}
                        onChange={(e) => setCustomerSearchQuery(e.target.value)}
                        className="bg-gray-50 border-gray-300"
                        autoFocus
                        data-testid="customer-search-input"
                      />
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {searchingCustomer ? (
                        <div className="p-4 text-center text-gray-500">Searching...</div>
                      ) : customerSearchResults.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                          {customerSearchQuery.length < 3 
                            ? 'Enter at least 3 characters to search' 
                            : 'No customer found'}
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {customerSearchResults.map((cust) => (
                            <div
                              key={cust.id}
                              onClick={() => selectCustomer(cust)}
                              className="p-3 hover:bg-emerald-50 cursor-pointer flex items-center gap-3"
                            >
                              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                                <User className="w-4 h-4 text-gray-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-800">{cust.name}</p>
                                <p className="text-xs text-gray-500">{cust.mobile}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {customerSearchQuery.length >= 3 && customerSearchResults.length === 0 && !searchingCustomer && (
                      <div className="p-3 border-t border-gray-200">
                        <Button 
                          onClick={() => {
                            setNewCustomerMobile(customerSearchQuery);
                            setCustomerSearchOpen(false);
                            setAddCustomerOpen(true);
                          }}
                          className="w-full bg-emerald-500 hover:bg-emerald-600"
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Add New Customer
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setAddCustomerOpen(true)}
                  className="border-gray-300"
                  data-testid="add-customer-btn"
                >
                  <UserPlus className="w-4 h-4" />
                </Button>
              </div>
            )}
            
            {/* GST Bill Toggle */}
            {selectedCustomer && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-gray-700">GST Tax Invoice</Label>
                  <Switch
                    checked={isGstBill}
                    onCheckedChange={setIsGstBill}
                    data-testid="gst-bill-toggle"
                  />
                </div>
                {isGstBill && (
                  <div className="mt-3 space-y-2">
                    <Input
                      value={customerGstin}
                      onChange={(e) => setCustomerGstin(e.target.value.toUpperCase())}
                      placeholder="GSTIN (e.g., 29ABCDE1234F1Z5)"
                      className="bg-white border-gray-300 text-sm"
                      data-testid="customer-gstin-input"
                    />
                    <Input
                      value={customerAddress}
                      onChange={(e) => setCustomerAddress(e.target.value)}
                      placeholder="Customer Address"
                      className="bg-white border-gray-300 text-sm"
                      data-testid="customer-address-input"
                    />
                    <select
                      value={customerState}
                      onChange={(e) => setCustomerState(e.target.value)}
                      className="w-full p-2 text-sm border border-gray-300 rounded-md bg-white"
                      data-testid="customer-state-select"
                    >
                      <option value="">Select State</option>
                      <option value="Andhra Pradesh">Andhra Pradesh</option>
                      <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                      <option value="Assam">Assam</option>
                      <option value="Bihar">Bihar</option>
                      <option value="Chhattisgarh">Chhattisgarh</option>
                      <option value="Delhi">Delhi</option>
                      <option value="Goa">Goa</option>
                      <option value="Gujarat">Gujarat</option>
                      <option value="Haryana">Haryana</option>
                      <option value="Himachal Pradesh">Himachal Pradesh</option>
                      <option value="Jharkhand">Jharkhand</option>
                      <option value="Karnataka">Karnataka</option>
                      <option value="Kerala">Kerala</option>
                      <option value="Madhya Pradesh">Madhya Pradesh</option>
                      <option value="Maharashtra">Maharashtra</option>
                      <option value="Manipur">Manipur</option>
                      <option value="Meghalaya">Meghalaya</option>
                      <option value="Mizoram">Mizoram</option>
                      <option value="Nagaland">Nagaland</option>
                      <option value="Odisha">Odisha</option>
                      <option value="Punjab">Punjab</option>
                      <option value="Rajasthan">Rajasthan</option>
                      <option value="Sikkim">Sikkim</option>
                      <option value="Tamil Nadu">Tamil Nadu</option>
                      <option value="Telangana">Telangana</option>
                      <option value="Tripura">Tripura</option>
                      <option value="Uttar Pradesh">Uttar Pradesh</option>
                      <option value="Uttarakhand">Uttarakhand</option>
                      <option value="West Bengal">West Bengal</option>
                    </select>
                    <p className="text-xs text-amber-600">
                      {customerState && customerState.toLowerCase() !== 'tamil nadu' 
                        ? '⚠️ Inter-state sale: IGST will be applied' 
                        : 'Intra-state sale: CGST + SGST will be applied'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Totals & Actions */}
        <div className="w-80 flex flex-col bg-gray-50">
          {/* Totals */}
          <div className="flex-1 p-4 space-y-3 overflow-y-auto">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Items</span>
              <span>{cartItems.length}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Subtotal <span className="text-xs text-gray-400">(incl. GST)</span></span>
              <span>₹{cartTotals.subtotal.toFixed(2)}</span>
            </div>
            {cartTotals.discount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Discount</span>
                <span>-₹{cartTotals.discount.toFixed(2)}</span>
              </div>
            )}
            
            {/* Tax Group-wise Summary - GST Inclusive */}
            {Object.keys(taxGroupSummary).length > 0 && (
              <div className="border-t border-gray-200 pt-2 mt-2">
                <p className="text-xs font-semibold text-gray-700 mb-2">Tax Breakup <span className="font-normal text-gray-400">(included in price)</span></p>
                {Object.entries(taxGroupSummary).map(([groupName, data]) => (
                  <div key={groupName} className="mb-2 p-2 bg-white rounded border border-gray-100">
                    <div className="flex justify-between text-xs text-gray-700 font-medium mb-1">
                      <span>{groupName}</span>
                      <span>₹{data.total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Taxable: ₹{data.taxable.toFixed(2)}</span>
                      <span>CGST: ₹{data.cgst.toFixed(2)} | SGST: ₹{data.sgst.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex justify-between text-sm text-gray-600">
              <span>Total Tax</span>
              <span data-testid="cart-total-tax">₹{cartTotals.tax.toFixed(2)}</span>
            </div>
            
            {/* Bill Discount */}
            {/* Bill Discount - Only show if allowed for this store */}
            {discountSettings.allow_bill_discount && (
            <div className="flex justify-between items-center text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Bill Discount</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDiscountDialogOpen(true)}
                  className="h-6 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                  disabled={cartItems.length === 0}
                  data-testid="add-discount-btn"
                >
                  <Tag className="w-3 h-3 mr-1" />
                  {billDiscountAmount > 0 ? 'Edit' : 'Add'}
                </Button>
                <span className="text-xs text-gray-400">(Max {discountSettings.max_discount_percent}%)</span>
              </div>
              {billDiscountAmount > 0 ? (
                <div className="flex items-center gap-1">
                  <span className="text-red-600 font-medium">-₹{billDiscountAmount.toFixed(2)}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearDiscount}
                    className="h-5 w-5 p-0 text-gray-400 hover:text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <span className="text-gray-400">₹0.00</span>
              )}
            </div>
            )}
            {discountSettings.allow_bill_discount && billDiscount.reason && (
              <p className="text-xs text-gray-500 italic">Reason: {billDiscount.reason}</p>
            )}
            
            {roundOff !== 0 && (
              <div className="flex justify-between text-sm text-gray-600">
                <span>Round Off</span>
                <span>{roundOff > 0 ? '+' : ''}₹{roundOff.toFixed(2)}</span>
              </div>
            )}
            <div className="border-t border-gray-300 pt-3">
              <div className="flex justify-between text-xl font-bold text-gray-800">
                <span>Net Total</span>
                <span>₹{grandTotal.toLocaleString()}</span>
              </div>
            </div>
            
            {/* Savings Display - includes MRP savings + item discounts + bill discount */}
            {(cartTotals.savings + billDiscountAmount) > 0 && (
              <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-green-700">🎉 Your Savings</span>
                  <span className="text-lg font-bold text-green-600">₹{(cartTotals.savings + billDiscountAmount).toFixed(2)}</span>
                </div>
                <p className="text-xs text-green-600 mt-1">
                  {billDiscountAmount > 0 
                    ? `MRP discounts + Bill discount (${billDiscount.type === 'percent' ? billDiscount.value + '%' : '₹' + billDiscount.value})`
                    : 'You saved on MRP discounts!'
                  }
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-4 border-t border-gray-200 space-y-2">
            <Button
              onClick={handlePayClick}
              disabled={cartItems.length === 0}
              className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 text-white text-lg"
              data-testid="pay-btn"
            >
              <Receipt className="w-5 h-5 mr-2" />
              Pay ₹{grandTotal.toLocaleString()}
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setHoldDialogOpen(true)}
                disabled={cartItems.length === 0}
                className="flex-1 border-gray-300"
              >
                <Pause className="w-4 h-4 mr-1" /> Hold
              </Button>
              <Button
                variant="outline"
                onClick={() => { setCartItems([]); setOfferPayMode('cash'); }}
                disabled={cartItems.length === 0}
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
              >
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent 
          className="bg-white border-gray-200 max-w-lg"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            // Focus cash input when dialog opens
            setTimeout(() => cashInputRef.current?.focus(), 100);
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-emerald-500" />
                Payment
              </span>
              <span className="text-2xl font-bold text-emerald-600" data-testid="payment-dialog-grand-total">₹{grandTotal.toLocaleString()}</span>
            </DialogTitle>
          </DialogHeader>
          
          {/* Keyboard shortcut hint */}
          <p className="text-xs text-gray-500 -mt-2">
            Split freely across modes. Type a partial amount and press Enter — the remaining balance shows below and the cursor jumps to the next mode.
            Leave the field empty and press Enter to skip this mode without adding a row.
          </p>

          {/* Pay-by selector — only when a slab offer prices differently per payment mode */}
          {hasPayModeOffer && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2" data-testid="offer-paymode-selector">
              <p className="text-xs font-medium text-indigo-800 mb-1.5">Offer price depends on payment mode — bill total by mode:</p>
              <div className="grid grid-cols-3 gap-1.5">
                {['cash', 'card', 'upi'].map(mode => (
                  <button
                    key={mode}
                    type="button"
                    tabIndex={-1}
                    onClick={() => changeOfferPayMode(mode)}
                    className={`rounded-md border px-2 py-1.5 text-sm transition-colors ${offerPayMode === mode
                      ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-indigo-200 text-indigo-800 hover:bg-indigo-100'}`}
                    data-testid={`offer-paymode-${mode}`}
                  >
                    <span className="capitalize">{mode}</span>
                    <span className="block font-bold">₹{grandTotalForMode(mode).toLocaleString()}</span>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-indigo-700 mt-1">Switches automatically to the mode you pay the most with.</p>
            </div>
          )}

          {/* Common balance banner — single source of truth for the running balance */}
          <div
            className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${
              balanceDue > 0 ? 'border-amber-300 bg-amber-50' : 'border-emerald-300 bg-emerald-50'
            }`}
            data-testid="payment-balance-banner"
          >
            <span className={balanceDue > 0 ? 'text-amber-800' : 'text-emerald-800'}>
              {balanceDue > 0 ? 'Balance to collect' : (balanceDue < 0 ? 'Change to return' : 'Fully paid')}
            </span>
            <span className={`text-lg font-bold ${balanceDue > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
              ₹{Math.abs(balanceDue).toFixed(2)}
            </span>
          </div>
          
          <div className="space-y-4">
            {/* Split payment - any combination of modes for one bill */}
            <div className="space-y-2" data-testid="payment-modes">
              {/* Cash */}
              <div className="flex items-center gap-2">
                <Label className="text-gray-700 flex items-center gap-1 w-20 shrink-0">
                  <Banknote className="w-4 h-4 text-green-600" /> Cash
                </Label>
                <Input
                  ref={cashInputRef}
                  type="number"
                  value={cashReceived}
                  onChange={(e) => setCashReceived(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addAndAdvance('cash', cashReceived, '', cardAmountRef);
                    }
                  }}
                  placeholder="Amount — press Enter to add"
                  className="bg-gray-50 border-gray-300"
                  data-testid="cash-input"
                />
                <Button
                  size="sm"
                  onClick={() => addAndAdvance('cash', cashReceived, '', cardAmountRef)}
                  className="bg-green-500 hover:bg-green-600 shrink-0 hidden"
                  data-testid="add-cash-btn"
                  tabIndex={-1}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex gap-1 pl-[88px]">
                {[100, 500, 2000].map(amt => (
                  <Button key={amt} size="sm" variant="outline" tabIndex={-1}
                    onClick={() => setCashReceived(String(amt))} className="flex-1 text-xs">
                    ₹{amt}
                  </Button>
                ))}
                <Button size="sm" variant="outline" tabIndex={-1}
                  onClick={() => setCashReceived(balanceDue > 0 ? balanceDue.toFixed(2) : grandTotal.toFixed(2))}
                  className="flex-1 text-xs" data-testid="cash-exact-btn">
                  Balance
                </Button>
              </div>

              {/* Card */}
              <div className="flex items-center gap-2">
                <Label className="text-gray-700 flex items-center gap-1 w-20 shrink-0">
                  <CreditCard className="w-4 h-4 text-blue-600" /> Card
                </Label>
                <Input
                  ref={cardAmountRef}
                  type="number"
                  value={cardAmount}
                  onChange={(e) => setCardAmount(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      cardRefRef.current?.focus();
                    }
                  }}
                  placeholder="Amount — Enter → Ref"
                  className="bg-gray-50 border-gray-300"
                  data-testid="card-amount-input"
                />
                <Input
                  ref={cardRefRef}
                  value={cardReference}
                  onChange={(e) => setCardReference(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addAndAdvance('card', cardAmount, cardReference, upiAmountRef);
                    }
                  }}
                  placeholder="Last 4 digits"
                  className="bg-gray-50 border-gray-300 w-32"
                  data-testid="card-ref-input"
                />
                <Button
                  size="sm"
                  onClick={() => addAndAdvance('card', cardAmount, cardReference, upiAmountRef)}
                  className="bg-blue-500 hover:bg-blue-600 shrink-0 hidden"
                  data-testid="add-card-btn"
                  tabIndex={-1}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* UPI */}
              <div className="flex items-center gap-2">
                <Label className="text-gray-700 flex items-center gap-1 w-20 shrink-0">
                  <Smartphone className="w-4 h-4 text-purple-600" /> UPI
                </Label>
                <Input
                  ref={upiAmountRef}
                  type="number"
                  value={upiAmount}
                  onChange={(e) => setUpiAmount(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      upiRefRef.current?.focus();
                    }
                  }}
                  placeholder="Amount — Enter → Txn"
                  className="bg-gray-50 border-gray-300"
                  data-testid="upi-amount-input"
                />
                <Input
                  ref={upiRefRef}
                  value={upiReference}
                  onChange={(e) => setUpiReference(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addAndAdvance('upi', upiAmount, upiReference, completeSaleButtonRef);
                    }
                  }}
                  placeholder="Txn ID"
                  className="bg-gray-50 border-gray-300 w-32"
                  data-testid="upi-ref-input"
                />
                <Button
                  size="sm"
                  onClick={() => addAndAdvance('upi', upiAmount, upiReference, completeSaleButtonRef)}
                  className="bg-purple-500 hover:bg-purple-600 shrink-0 hidden"
                  data-testid="add-upi-btn"
                  tabIndex={-1}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Added Payments */}
            {payments.length > 0 && (
              <div className="space-y-2">
                <Label className="text-gray-700">Payments Added</Label>
                <div className="space-y-1">
                  {payments.map((pay, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded" data-testid={`payment-row-${pay.mode}`}>
                      <div className="flex items-center gap-2">
                        {pay.mode === 'cash' && <Banknote className="w-4 h-4 text-green-500" />}
                        {pay.mode === 'card' && <CreditCard className="w-4 h-4 text-blue-500" />}
                        {pay.mode === 'upi' && <Smartphone className="w-4 h-4 text-purple-500" />}
                        <span className="capitalize">{pay.mode}</span>
                        {pay.reference && <span className="text-xs text-gray-500">({pay.reference})</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium" data-testid={`payment-amount-${pay.mode}`}>₹{pay.amount.toLocaleString()}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removePayment(idx)}
                          className="h-6 w-6 p-0 text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Paid / Balance */}
            <div className={`p-4 rounded-lg space-y-2 ${balanceDue > 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Bill total</span>
                <span className="font-medium text-gray-800">₹{grandTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Paid</span>
                <span className="font-medium text-gray-800" data-testid="total-paid">₹{totalPaid.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center border-t border-white/60 pt-2">
                <span className={balanceDue > 0 ? 'text-amber-800' : 'text-green-800'}>
                  {balanceDue > 0 ? 'Balance Due' : 'Change'}
                </span>
                <span className={`text-2xl font-bold ${balanceDue > 0 ? 'text-amber-700' : 'text-green-700'}`}
                  data-testid="balance-due">
                  ₹{Math.abs(balanceDue).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)} className="border-gray-300" tabIndex={-1}>
              Cancel
            </Button>
            <Button 
              ref={completeSaleButtonRef}
              onClick={completeSale}
              disabled={processingPayment || balanceDue > 0}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="complete-sale-btn"
            >
              {processingPayment ? 'Processing...' : 'Complete Sale (Enter)'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hold Bill Dialog */}
      <Dialog open={holdDialogOpen} onOpenChange={setHoldDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-gray-800">Hold Bill</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-700">Note (Optional)</Label>
              <Textarea
                value={holdNote}
                onChange={(e) => setHoldNote(e.target.value)}
                placeholder="Add a note for this held bill..."
                className="bg-gray-50 border-gray-300"
                data-testid="hold-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHoldDialogOpen(false)}>Cancel</Button>
            <Button onClick={holdBill} className="bg-amber-500 hover:bg-amber-600" data-testid="confirm-hold-bill-btn">Hold Bill</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Held Bills Dialog */}
      <Dialog open={heldBillsDialogOpen} onOpenChange={setHeldBillsDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-gray-800">Held Bills</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {heldBills.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No held bills</p>
            ) : (
              heldBills.map((bill) => (
                <div key={bill.id} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800">
                      {bill.customer_name || bill.customer_phone || 'Walk-in'}
                    </p>
                    <p className="text-sm text-gray-500">{bill.items?.length} items | ₹{bill.subtotal?.toLocaleString()}</p>
                    {bill.hold_note && (
                      <p className="text-xs text-gray-400 italic">{bill.hold_note}</p>
                    )}
                  </div>
                  <Button 
                    onClick={() => recallHeldBill(bill)}
                    className="bg-emerald-500 hover:bg-emerald-600"
                    data-testid={`recall-held-bill-btn-${bill.id}`}
                  >
                    <Play className="w-4 h-4 mr-1" /> Recall
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Dialog with Receipt Preview */}
      <Dialog open={successDialogOpen} onOpenChange={(open) => { if (!open) resetAfterBill(); }}>
        <DialogContent className="bg-white border-gray-200 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="w-4 h-4 text-green-600" />
              </div>
              Sale Complete - Receipt Preview
            </DialogTitle>
          </DialogHeader>
          
          {/* Receipt Preview */}
          <div className="flex justify-center py-4 bg-gray-50 rounded-lg">
            <Receipt 
              ref={receiptRef}
              sale={lastSaleFull} 
              storeInfo={storeInfo}
            />
          </div>
          
          {/* Hidden A4 GST Invoice for PDF generation */}
          {isGstBill && lastSaleFull?.customer_gstin && (
            <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
              <A4GSTInvoice 
                ref={a4InvoiceRef}
                sale={lastSaleFull}
                storeInfo={storeInfo}
                gstSettings={gstInvoiceSettings}
              />
            </div>
          )}
          
          <div className="text-center text-sm text-gray-500 mb-2">
            Press <kbd className="px-2 py-1 bg-gray-100 rounded border">Enter</kbd> to print
          </div>
          
          <DialogFooter className="flex flex-col gap-2">
            {/* A4 GST Invoice Download - Only show if GST billing was enabled and customer has GSTIN */}
            {isGstBill && lastSaleFull?.customer_gstin && (
              <Button 
                onClick={downloadA4GSTInvoice}
                variant="outline"
                className="w-full border-blue-300 text-blue-600 hover:bg-blue-50"
                data-testid="download-gst-invoice-btn"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Download A4 GST Invoice (PDF)
              </Button>
            )}
            
            <div className="flex gap-2 w-full justify-center">
              <Button 
                variant="outline"
                onClick={resetAfterBill}
                className="border-gray-300"
              >
                Skip Print
              </Button>
              <Button 
                onClick={() => {
                  handlePrintReceipt();
                  setTimeout(() => resetAfterBill(), 500);
                }}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="print-receipt-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print Receipt
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Customer Dialog */}
      <Dialog open={addCustomerOpen} onOpenChange={setAddCustomerOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-500" />
              Add New Customer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-700">Mobile Number *</Label>
              <Input
                value={newCustomerMobile}
                onChange={(e) => setNewCustomerMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="Enter 10-digit mobile"
                className="bg-gray-50 border-gray-300"
                data-testid="new-customer-mobile-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700">Customer Name *</Label>
              <Input
                value={newCustomerName}
                onChange={(e) => setNewCustomerName(e.target.value)}
                placeholder="Enter customer name"
                className="bg-gray-50 border-gray-300"
                data-testid="new-customer-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setAddCustomerOpen(false);
                setNewCustomerName('');
                setNewCustomerMobile('');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={addNewCustomer}
              disabled={addingCustomer || !newCustomerMobile || !newCustomerName}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="save-new-customer-btn"
            >
              {addingCustomer ? 'Adding...' : 'Add Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pre-Payment Customer Prompt (shown on Pay click when no customer is selected) */}
      <Dialog
        open={prePayOpen}
        onOpenChange={(open) => {
          if (!open) {
            setPrePayOpen(false);
            // If dialog was closed by an explicit action (select/skip/create),
            // those handlers already dispatched to payment. Only treat an
            // overlay/Esc dismiss as an implicit skip-to-guest.
            if (!prePayDispatchedRef.current) {
              prePayDispatchedRef.current = true;
              setTimeout(() => openPaymentDialog(), 50);
            }
          }
        }}
      >
        <DialogContent
          className="bg-white border-gray-200 max-w-md"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              prePaySkipAsGuest();
            }
          }}
          data-testid="prepay-customer-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-500" />
              Customer Details
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-gray-700 text-sm">Mobile Number</Label>
              <Input
                ref={prePayMobileRef}
                autoFocus
                value={prePayMobile}
                onChange={(e) => setPrePayMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const exact = prePayResults.find(c => c.mobile === prePayMobile);
                    if (exact) {
                      prePaySelectAndPay(exact);
                    } else if (prePayResults.length === 1) {
                      prePaySelectAndPay(prePayResults[0]);
                    } else if (prePayMobile.length >= 10) {
                      prePayNameRef.current?.focus();
                    }
                  }
                }}
                placeholder="Search by mobile (auto-search)"
                className="bg-gray-50 border-gray-300"
                data-testid="prepay-mobile-input"
              />
            </div>

            {/* Live search results */}
            {prePayMobile.length >= 3 && (
              <div className="border border-gray-200 rounded-md max-h-40 overflow-y-auto">
                {prePaySearching ? (
                  <div className="p-3 text-xs text-gray-500">Searching...</div>
                ) : prePayResults.length > 0 ? (
                  prePayResults.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => prePaySelectAndPay(c)}
                      className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-gray-100 last:border-0"
                      data-testid={`prepay-result-${c.mobile}`}
                    >
                      <div className="text-sm font-medium text-gray-800">{c.name}</div>
                      <div className="text-xs text-gray-500">{c.mobile}{c.created_at_store_code ? ` · ${c.created_at_store_code}` : ''}</div>
                    </button>
                  ))
                ) : (
                  <div className="p-3 text-xs text-gray-500">
                    No match. Enter name below to add this customer.
                  </div>
                )}
              </div>
            )}

            {/* New customer name field - only matters if no exact match */}
            <div className="space-y-1">
              <Label className="text-gray-700 text-sm">Customer Name <span className="text-gray-400">(for new customer)</span></Label>
              <Input
                ref={prePayNameRef}
                value={prePayName}
                onChange={(e) => setPrePayName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    prePayCreateAndPay();
                  }
                }}
                placeholder="Enter name to add new customer"
                className="bg-gray-50 border-gray-300"
                data-testid="prepay-name-input"
              />
            </div>

            <div className="text-xs text-gray-500 flex items-center justify-between pt-1">
              <span>Press <kbd className="px-1.5 py-0.5 bg-gray-100 rounded border">Esc</kbd> to skip (bill as Guest)</span>
              <span>Press <kbd className="px-1.5 py-0.5 bg-gray-100 rounded border">Enter</kbd> to continue</span>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={prePaySkipAsGuest}
              className="border-gray-300"
              data-testid="prepay-skip-btn"
            >
              Skip (Guest)
            </Button>
            <Button
              onClick={prePayCreateAndPay}
              disabled={prePaySavingNew || prePayMobile.length < 10 || !prePayName.trim()}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="prepay-save-pay-btn"
            >
              {prePaySavingNew ? 'Saving...' : 'Save & Pay'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discount Dialog */}
      <Dialog open={discountDialogOpen} onOpenChange={setDiscountDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="w-5 h-5 text-emerald-600" />
              Apply Bill Discount
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Button
                variant={discountType === 'percent' ? 'default' : 'outline'}
                onClick={() => setDiscountType('percent')}
                className={discountType === 'percent' ? 'bg-emerald-500' : ''}
              >
                <Percent className="w-4 h-4 mr-1" /> Percentage
              </Button>
              <Button
                variant={discountType === 'amount' ? 'default' : 'outline'}
                onClick={() => setDiscountType('amount')}
                className={discountType === 'amount' ? 'bg-emerald-500' : ''}
              >
                ₹ Fixed Amount
              </Button>
            </div>
            <div>
              <Label>Discount {discountType === 'percent' ? '(%)' : '(₹)'}</Label>
              <Input
                type="number"
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                placeholder={discountType === 'percent' ? 'Enter percentage' : 'Enter amount'}
                className="mt-1"
                min="0"
                max={discountType === 'percent' ? '100' : cartTotals.total}
                data-testid="discount-value-input"
              />
            </div>
            <div>
              <Label>Reason (Optional)</Label>
              <Input
                value={discountReason}
                onChange={(e) => setDiscountReason(e.target.value)}
                placeholder="e.g., Regular customer, Damaged item"
                className="mt-1"
                data-testid="discount-reason-input"
              />
            </div>
            {discountValue && (
              <div className="p-3 bg-emerald-50 rounded-lg">
                <p className="text-sm text-emerald-700">
                  Discount: {discountType === 'percent' ? `${discountValue}%` : `₹${discountValue}`}
                  {discountType === 'percent' && ` = ₹${(cartTotals.total * parseFloat(discountValue || 0) / 100).toFixed(2)}`}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscountDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={applyDiscount}
              className="bg-emerald-500 hover:bg-emerald-600"
              disabled={!discountValue || parseFloat(discountValue) <= 0}
              data-testid="apply-discount-btn"
            >
              Apply Discount
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bill Lookup Dialog (Re-print / Return / Exchange) */}
      <Dialog open={billLookupDialogOpen} onOpenChange={setBillLookupDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSearch className="w-5 h-5 text-blue-600" />
              {billAction === 'reprint' && 'Re-print Bill'}
              {billAction === 'return' && 'Process Return'}
              {billAction === 'exchange' && 'Process Exchange'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Filters Row */}
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-gray-500">Search (Bill No. / Mobile)</Label>
                <Input
                  value={billLookupQuery}
                  onChange={(e) => setBillLookupQuery(e.target.value)}
                  placeholder="Bill number or customer mobile"
                  className="mt-1"
                  onKeyDown={(e) => e.key === 'Enter' && fetchBillsList()}
                  data-testid="bill-lookup-input"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">From Date</Label>
                <Input
                  type="date"
                  value={billDateFrom}
                  onChange={(e) => setBillDateFrom(e.target.value)}
                  className="mt-1 w-[150px]"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">To Date</Label>
                <Input
                  type="date"
                  value={billDateTo}
                  onChange={(e) => setBillDateTo(e.target.value)}
                  className="mt-1 w-[150px]"
                />
              </div>
              <Button 
                onClick={fetchBillsList}
                disabled={loadingBills}
                className="bg-blue-500 hover:bg-blue-600"
              >
                <Search className="w-4 h-4 mr-1" />
                {loadingBills ? 'Loading...' : 'Search'}
              </Button>
            </div>
            
            {/* Bills Table */}
            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader className="bg-gray-50 sticky top-0">
                    <TableRow>
                      <TableHead className="w-[160px]">Date & Time</TableHead>
                      <TableHead>Customer / Mobile</TableHead>
                      <TableHead className="text-right">Bill Value</TableHead>
                      <TableHead>Bill Number</TableHead>
                      <TableHead className="text-center">Items</TableHead>
                      <TableHead className="text-center">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingBills ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                          Loading bills...
                        </TableCell>
                      </TableRow>
                    ) : billsList.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                          No bills found. Try adjusting the date range or search query.
                        </TableCell>
                      </TableRow>
                    ) : (
                      billsList.map((bill) => (
                        <TableRow key={bill.id} className="hover:bg-gray-50">
                          <TableCell className="text-sm">
                            <div>{bill.bill_datetime?.split('T')[0]}</div>
                            <div className="text-xs text-gray-500">
                              {bill.bill_datetime?.split('T')[1]?.substring(0, 5)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{bill.customer_name || 'Walk-in'}</div>
                            {bill.customer_phone && (
                              <div className="text-xs text-gray-500">{bill.customer_phone}</div>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-emerald-600">
                            ₹{bill.grand_total?.toLocaleString()}
                          </TableCell>
                          <TableCell className="font-mono text-sm">{bill.bill_number}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{bill.items?.length || 0}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {billAction === 'reprint' && (
                              <Button
                                size="sm"
                                onClick={() => handleReprint(bill)}
                                className="bg-blue-500 hover:bg-blue-600 text-white h-8"
                              >
                                <Printer className="w-3 h-3 mr-1" /> Print
                              </Button>
                            )}
                            {billAction === 'return' && (
                              <Button
                                size="sm"
                                onClick={() => handleReturn(bill)}
                                className="bg-red-500 hover:bg-red-600 text-white h-8"
                              >
                                <RotateCcw className="w-3 h-3 mr-1" /> Return
                              </Button>
                            )}
                            {billAction === 'exchange' && (
                              <Button
                                size="sm"
                                onClick={() => handleExchange(bill)}
                                className="bg-amber-500 hover:bg-amber-600 text-white h-8"
                              >
                                <ArrowLeftRight className="w-3 h-3 mr-1" /> Exchange
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
            
            {billsList.length > 0 && (
              <div className="text-sm text-gray-500 text-right">
                Showing {billsList.length} bill(s)
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBillLookupDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return/Refund Dialog */}
      <ReturnRefundDialog
        open={returnDialogOpen}
        onOpenChange={setReturnDialogOpen}
        bill={selectedBillForReturn}
        onSuccess={handleReturnSuccess}
      />

      {/* Exchange Dialog */}
      <ExchangeDialog
        open={exchangeDialogOpen}
        onOpenChange={setExchangeDialogOpen}
        bill={selectedBillForExchange}
        storeId={storeId}
        onSuccess={handleExchangeSuccess}
      />

      {/* Offer Picker Dialog */}
      <Dialog open={offerPickerOpen} onOpenChange={setOfferPickerOpen}>
        <DialogContent className="max-w-md" data-testid="offer-picker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">🎁 Choose an offer</DialogTitle>
          </DialogHeader>
          {offerPickerIndex != null && cartItems[offerPickerIndex] && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                <span className="font-medium">{cartItems[offerPickerIndex].product_name}</span>
                {' · '}Qty {cartItems[offerPickerIndex].quantity} · ₹{Number(cartItems[offerPickerIndex].unit_price).toFixed(2)}
              </p>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {[...(cartItems[offerPickerIndex].available_offers || [])]
                  .map(o => ({ o, d: computeOfferDiscount(o, cartItems[offerPickerIndex].unit_price, cartItems[offerPickerIndex].quantity, cartItems[offerPickerIndex].mrp) }))
                  .sort((x, y) => y.d - x.d)
                  .map(({ o, d }, idx) => {
                  const selected = cartItems[offerPickerIndex].applied_offer_id === o.id;
                  const isBest = idx === 0 && d > 0;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => applyChosenOffer(offerPickerIndex, o.id)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${selected ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/50'}`}
                      data-testid={`offer-option-${o.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{o.name}{isBest && <span className="ml-2 text-[10px] uppercase tracking-wide bg-emerald-600 text-white rounded px-1.5 py-0.5 align-middle" data-testid="offer-option-best">Best</span>}</p>
                          <p className="text-xs text-gray-500">
                            {o.offer_type === 'percentage_discount' && `${o.discount_value}% off${o.max_discount ? ` (cap ₹${o.max_discount})` : ''}`}
                            {o.offer_type === 'flat_discount' && `₹${o.discount_value} off per unit`}
                            {o.offer_type === 'buy_x_get_y' && `Buy ${o.buy_qty} get ${o.get_qty} free`}
                            {o.offer_type === 'qty_slab' && (
                              [...(o.tiers || [])].sort((a, b) => Number(a.min_qty) - Number(b.min_qty)).map(t => {
                                const modes = Object.entries(t.payment_modes || {});
                                const fmt = (v) => t.value_type === 'percent' ? `${v}%` : `₹${Number(v).toLocaleString()}`;
                                return `${Number(t.min_qty)}+ pcs: ${fmt(t.value)}${t.value_type === 'unit_price' ? '/pc' : t.value_type === 'flat_discount' ? ' off/pc' : t.value_type === 'percent' ? ' off' : ''}${modes.length ? ` (${modes.map(([m, v]) => `${m} ${fmt(v)}`).join(', ')})` : ''}`;
                              }).join(' · ')
                            )}
                            {o.code && <span className="ml-1 opacity-70">· {o.code}</span>}
                          </p>
                        </div>
                        <div className="text-right ml-3 shrink-0">
                          <p className="text-sm font-bold text-emerald-600">-₹{d.toFixed(2)}</p>
                          {selected && <p className="text-xs text-emerald-700">Applied</p>}
                        </div>
                      </div>
                    </button>
                  );
                })}
                {/* "No offer" option */}
                <button
                  type="button"
                  onClick={() => applyChosenOffer(offerPickerIndex, null)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${!cartItems[offerPickerIndex].applied_offer_id ? 'bg-gray-100 border-gray-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                  data-testid="offer-option-none"
                >
                  <p className="text-sm text-gray-700">No offer — use default price</p>
                </button>
              </div>
              <p className="text-xs text-gray-400">Only one offer can be applied per line. Offers are re-checked whenever the quantity changes — you'll be asked again if a different offer becomes better.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSBillingPage;
